<template>
  <nav class="pd-navbar" :class="{ 'pd-navbar-solid': !isHomePage || isScrolled }">
    <div class="pd-navbar-container">
      <!-- Left: Logo -->
      <router-link to="/" class="pd-navbar-logo">
        <img src="/pd-logo-white.png" alt="Point of Demon" />
      </router-link>

      <!-- Center: Navigation Links -->
      <div class="pd-navbar-center">
        <router-link to="/home" class="pd-nav-link">新品上市</router-link>
        <router-link to="/products" class="pd-nav-link">所有商品</router-link>
        <router-link to="/about" class="pd-nav-link">品牌理念</router-link>
      </div>

      <!-- Right: Icons -->
      <div class="pd-navbar-right">
        <!-- User Icon (now a direct link) -->
        <router-link to="/profile" class="pd-icon-btn">
          <i class="fas fa-user"></i>
        </router-link>

        <!-- Cart Icon -->
        <div class="pd-cart-icon" @click.stop.prevent="toggleSideCart">
          <i class="fas fa-shopping-cart"></i>
          <span v-if="cartItemNumber > 0" class="pd-cart-badge">{{ cartItemNumber }}</span>
        </div>

        <!-- Mobile Menu Toggle -->
        <button class="pd-mobile-toggle" @click="toggleMobileMenu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div class="pd-mobile-menu" :class="{ 'pd-mobile-menu-open': isMobileMenuOpen }">
      <router-link to="/home" class="pd-mobile-link" @click.native="closeMobileMenu">新品上市</router-link>
      <router-link to="/products" class="pd-mobile-link" @click.native="closeMobileMenu">所有商品</router-link>
      <router-link to="/about" class="pd-mobile-link" @click.native="closeMobileMenu">品牌理念</router-link>
      <div class="pd-mobile-user">
        <template v-if="isLogin">
          <div class="pd-mobile-username">{{ currentUser.name }}</div>
          <router-link to="/profile" class="pd-mobile-link" @click.native="closeMobileMenu">個人資訊</router-link>
          <router-link
            v-if="currentUser.role === 'admin'"
            to="/admin/products"
            class="pd-mobile-link"
            @click.native="closeMobileMenu"
          >管理員後台</router-link>
          <a @click="logout" class="pd-mobile-link">登出</a>
        </template>
        <template v-else>
           <router-link to="/signin" class="pd-mobile-link" @click.native="closeMobileMenu">登入</router-link>
           <router-link to="/signup" class="pd-mobile-link" @click.native="closeMobileMenu">註冊</router-link>
        </template>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'EcNavbar',
  data () {
    return {
      isScrolled: false,
      isMobileMenuOpen: false
    }
  },
  computed: {
    cartItemNumber () {
      return this.$store.state.cartItemNumber
    },
    ...mapState(['currentUser', 'isLogin']),
    isHomePage () {
      return this.$route.name === 'home'
    }
  },
  mounted () {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll () {
      this.isScrolled = window.scrollY > 50
    },
    toggleSideCart () {
      this.$root.$emit('toggleSideCart')
    },
    toggleMobileMenu () {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    },
    closeMobileMenu () {
      this.isMobileMenuOpen = false
    },
    logout () {
      this.$store.commit('logout')
      this.$router.push('/signin')
      this.closeMobileMenu()
    }
  }
}
</script>

<style scoped>
/* Navbar Container */
.pd-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.5); /* 半透明深色背景 */
  backdrop-filter: blur(10px); /* 毛玻璃效果 */
  transition: all 0.3s ease;
  min-height: 100px; /* 背景最小高度 100px */
  display: flex;
  align-items: center;
}

.pd-navbar-solid {
  background-color: var(--pd-black); /* 捲動後變成不透明黑色 */
  backdrop-filter: none;
  padding: 0.5rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  min-height: auto; /* 捲動後恢復自動高度 */
}

.pd-navbar-container {
  width: 100%;
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Logo */
.pd-navbar-logo {
  height: 100px;
  transition: height 0.3s ease;
  flex-shrink: 0;
}

.pd-navbar-solid .pd-navbar-logo {
  height: 50px;
}

.pd-navbar-logo img {
  height: 100%;
  width: auto;
  object-fit: contain;
  object-position: center center;
  transform: translateY(10%);
}

/* Navigation Links */
.pd-navbar-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 3rem;
}

.pd-navbar-right {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: auto;
}

.pd-nav-link {
  color: var(--pd-white);
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  transition: opacity 0.2s ease;
  white-space: nowrap;
  text-align: center;
  display: flex;
  align-items: center;
}

.pd-nav-link:hover {
  opacity: 0.7;
}

/* Icon Buttons */
.pd-icon-btn {
  color: var(--pd-white);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.125rem;
  padding: 0.5rem;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pd-icon-btn:hover {
  opacity: 0.7;
}

/* Cart Icon */
.pd-cart-icon {
  position: relative;
  color: var(--pd-white);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.pd-cart-icon i {
  font-size: 1.125rem;
  line-height: 1;
  transition: font-size 0.3s ease;
}

.pd-cart-icon:hover {
  opacity: 0.7;
}

.pd-cart-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ff0000;
  color: var(--pd-white);
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

/* User Dropdown */
.pd-user-dropdown {
  position: relative;
}

.pd-user-btn {
  background: transparent;
  border: 1px solid var(--pd-white);
  color: var(--pd-white);
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pd-user-btn:hover {
  background-color: var(--pd-white);
  color: var(--pd-black);
}

.pd-dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: var(--pd-white);
  border: 1px solid var(--pd-black);
  min-width: 200px;
  box-shadow: var(--shadow-md);
}

.pd-user-dropdown:hover .pd-dropdown-menu {
  display: block;
}

.pd-dropdown-item {
  display: block;
  padding: 0.75rem 1rem;
  color: var(--pd-black);
  font-size: 0.875rem;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.pd-dropdown-item:hover {
  background-color: var(--pd-gray-light);
  opacity: 1;
}

.pd-dropdown-divider {
  height: 1px;
  background-color: var(--pd-gray-light);
  margin: 0.5rem 0;
}

/* Mobile Toggle */
.pd-mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  justify-content: center;
  align-items: center;
}

.pd-mobile-toggle span {
  width: 22px;
  height: 2px;
  background-color: var(--pd-white);
  transition: all 0.3s ease;
}

/* Shrink icons when scrolled (desktop and mobile) */
.pd-navbar-solid .pd-cart-icon i,
.pd-navbar-solid .pd-icon-btn i {
  font-size: 0.9rem;
}

.pd-navbar-solid .pd-mobile-toggle span {
  width: 18px;
}

/* Mobile Menu */
.pd-mobile-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: none;
  background-color: var(--pd-black);
  padding: 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  z-index: 999;
}

.pd-mobile-menu-open {
  max-height: 600px;
  padding: 1rem 2rem;
  overflow-y: auto;
}

.pd-mobile-link {
  display: block;
  color: var(--pd-white);
  padding: 1rem 0;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.pd-mobile-user {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.pd-mobile-username {
  color: var(--pd-white);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .pd-navbar-center,
  .pd-navbar-right .pd-icon-btn:first-child {
    display: none;
  }

  .pd-mobile-toggle,
  .pd-mobile-menu {
    display: flex;
  }

  .pd-mobile-menu {
    display: block;
  }

  .pd-navbar-logo {
    position: static;
    transform: none;
    height: 80px;
    display: flex;
    align-items: center;
  }

  .pd-navbar-solid .pd-navbar-logo {
    height: 60px;
  }

  .pd-navbar-right {
    gap: 1.5rem; /* Consistent spacing between icons */
  }

  .pd-mobile-toggle {
    margin-right: 0; /* Remove any extra margin */
  }

  .pd-navbar {
    padding: 1rem 0;
  }

  .pd-navbar-solid {
    padding: 0.75rem 0;
  }

  .pd-cart-icon,
  .pd-icon-btn,
  .pd-mobile-toggle {
    display: flex;
    align-items: center;
  }
}
</style>
